import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { CLIENT_TYPE_LABELS } from '../client-portal/clientPortalApi';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export default function AdminClientCreateDialog({ onClose, onCreated, token }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    client_type: 'payg',
    loan_purpose: '', property_address: '', loan_amount: '',
    notes: '',
    seed_checklist: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone || null,
        client_type: form.client_type,
        loan_purpose: form.loan_purpose || null,
        property_address: form.property_address || null,
        loan_amount: form.loan_amount ? Number(form.loan_amount) : null,
        notes: form.notes || null,
      };
      const res = await fetch(`${backendUrl}/api/admin/clients`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Could not create client');

      // Optionally seed checklist
      if (form.seed_checklist) {
        await fetch(`${backendUrl}/api/admin/clients/${data.id}/checklist/seed`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      onCreated?.(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl ring-1 ring-slate-200 overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">New client</h3>
          <button onClick={onClose} className="p-1 text-slate-500 hover:text-slate-900" aria-label="Close" data-testid="close-create-dialog">
            <X className="w-5 h-5" />
          </button>
        </header>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full name" required value={form.name} onChange={(v) => update('name', v)} testId="form-name" />
            <Field label="Email" type="email" required value={form.email} onChange={(v) => update('email', v)} testId="form-email" />
            <Field label="Phone" value={form.phone} onChange={(v) => update('phone', v)} testId="form-phone" />
            <SelectField
              label="Client type"
              value={form.client_type}
              onChange={(v) => update('client_type', v)}
              testId="form-type"
              options={Object.entries(CLIENT_TYPE_LABELS).map(([k, v]) => ({ value: k, label: v }))}
            />
            <Field label="Loan purpose" value={form.loan_purpose} onChange={(v) => update('loan_purpose', v)} testId="form-purpose" />
            <Field label="Loan amount (AUD)" type="number" value={form.loan_amount} onChange={(v) => update('loan_amount', v)} testId="form-amount" />
            <div className="sm:col-span-2">
              <Field label="Property address" value={form.property_address} onChange={(v) => update('property_address', v)} testId="form-property" />
            </div>
            <div className="sm:col-span-2">
              <Field label="Notes" textarea value={form.notes} onChange={(v) => update('notes', v)} testId="form-notes" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.seed_checklist}
              onChange={(e) => update('seed_checklist', e.target.checked)}
              data-testid="form-seed-checklist"
            />
            Send the smart document checklist for their client type
          </label>

          {err && <div className="text-rose-700 text-sm bg-rose-50 ring-1 ring-rose-200 rounded-md px-3 py-2">{err}</div>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
            <button
              type="submit"
              disabled={busy}
              data-testid="form-submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium disabled:opacity-60"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Create client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, textarea, testId }) {
  return (
    <label className="block text-sm">
      <span className="block text-slate-700 mb-1">{label}{required && <span className="text-rose-500"> *</span>}</span>
      {textarea ? (
        <textarea
          rows={3}
          required={required}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
        />
      ) : (
        <input
          type={type}
          required={required}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
        />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block text-sm">
      <span className="block text-slate-700 mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
